/* ============================================================
 * TUNExperience · Hub — capa de acceso al backend
 *
 * Único punto del frontend que habla con la API. Todo lo demás recibe
 * datos ya resueltos, así que no hay componentes construyendo URLs ni
 * repartiendo el token por la aplicación.
 *
 * Antes esta capa era síncrona y leía un objeto en memoria: `api.sede(id)`
 * devolvía la sede al instante y se llamaba dentro del render. Al pasar a
 * red, cada lectura tarda y puede fallar, así que devuelve promesas y la
 * carga la coordina estado.jsx.
 * ============================================================ */

// En producción se compila con VITE_API_URL apuntando al dominio del backend.
// En desarrollo queda vacío y las peticiones van por el proxy de Vite.
const BASE = import.meta.env.VITE_API_URL || '';

const CLAVE_TOKEN = 'tune_hub_token';
const CLAVE_SESION = 'tune_hub_sesion';

/**
 * Resuelve la dirección de una foto contra el backend.
 *
 * La base guarda rutas relativas ("/fotos/xxx.webp") para no atarse a un
 * dominio. El navegador las resolvería contra el dominio del hub, donde no
 * hay imágenes: el servidor de estáticos devolvería index.html con un 200 y
 * la foto se vería rota sin ningún 404 que lo explique.
 */
export const urlFoto = (u) => (u && u.startsWith('/') ? BASE + u : u);

export const sesionGuardada = () => {
  try {
    const bruto = localStorage.getItem(CLAVE_SESION);
    return bruto ? JSON.parse(bruto) : null;
  } catch {
    return null;   // localStorage bloqueado o JSON corrupto: se entra de nuevo
  }
};

export const guardarSesion = (token, sesion) => {
  localStorage.setItem(CLAVE_TOKEN, token);
  localStorage.setItem(CLAVE_SESION, JSON.stringify(sesion));
};

export const borrarSesion = () => {
  localStorage.removeItem(CLAVE_TOKEN);
  localStorage.removeItem(CLAVE_SESION);
};

/** Error con el mensaje que el backend quiso mostrar al usuario. */
export class ErrorApi extends Error {
  /**
   * `datos` es el cuerpo completo de la respuesta. Algunos rechazos traen
   * detalle además del mensaje —qué reservas estorban y a qué horas— y sin
   * esto la interfaz solo podría enseñar una frase suelta.
   */
  constructor(mensaje, estado, datos) {
    super(mensaje);
    this.estado = estado;
    this.datos = datos || null;
  }
}

async function pedir(ruta, { metodo = 'GET', cuerpo } = {}) {
  const token = localStorage.getItem(CLAVE_TOKEN);
  let res;
  try {
    res = await fetch(BASE + '/api' + ruta, {
      method: metodo,
      headers: {
        ...(cuerpo ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: cuerpo ? JSON.stringify(cuerpo) : undefined,
    });
  } catch {
    // fetch solo rechaza si no hubo respuesta: sin red, DNS o CORS.
    throw new ErrorApi('No se ha podido contactar con el servidor.', 0);
  }

  if (res.status === 401) {
    // El token caducó (duran 12 h) o dejó de ser válido. Se limpia y se
    // recarga para volver al login en vez de dejar la pantalla a medias.
    borrarSesion();
    window.location.reload();
    throw new ErrorApi('Sesión caducada.', 401);
  }

  const datos = await res.json().catch(() => null);
  if (!res.ok) {
    throw new ErrorApi(datos?.error || 'Ha ocurrido un error inesperado.', res.status, datos);
  }
  return datos;
}

export const api = {
  /* --- autenticación --- */
  loginGoogle: (credential) => pedir('/auth/google', { metodo: 'POST', cuerpo: { credential } }),
  loginDemo: (devEmail) => pedir('/auth/google', { metodo: 'POST', cuerpo: { devEmail } }),

  /* --- lectura --- */
  sedes: () => pedir('/hub/sedes'),
  sede: (id) => pedir(`/hub/sedes/${id}`),
  tablero: (id, fecha) => pedir(`/hub/sedes/${id}/tablero?fecha=${fecha}`),
  clientes: () => pedir('/hub/clientes'),
  metricas: () => pedir('/hub/metricas'),
  clientesAgregado: () => pedir('/hub/clientes-agregado'),

  /* --- reservas --- */
  crearReserva: (datos) => pedir('/hub/reservas', { metodo: 'POST', cuerpo: datos }),
  cambiarReserva: (id, accion) => pedir(`/hub/reservas/${id}`, { metodo: 'PATCH', cuerpo: { accion } }),

  /** Valida un pase en el mostrador. Admite el token del QR o el código corto. */
  validarPase: (clave) => pedir('/hub/pases/validar', { metodo: 'POST', cuerpo: { clave } }),

  /* --- clientes --- */
  crearCliente: (datos) => pedir('/hub/clientes', { metodo: 'POST', cuerpo: datos }),
  editarCliente: (id, datos) => pedir(`/hub/clientes/${id}`, { metodo: 'PATCH', cuerpo: datos }),
  venderBono: (clienteId, datos) => pedir(`/hub/clientes/${clienteId}/bonos`, { metodo: 'POST', cuerpo: datos }),

  /* --- mi sede --- */
  editarMiSede: (datos) => pedir('/hub/misede', { metodo: 'PATCH', cuerpo: datos }),
  cambiarTune: (id, estado) => pedir(`/hub/tunes/${id}`, { metodo: 'PATCH', cuerpo: { estado } }),
  /**
   * Sube una foto. Va como multipart, no como JSON: mandar la imagen en
   * base64 dentro de un JSON la infla un tercio y obliga a subir el límite
   * del cuerpo para todas las demás peticiones.
   *
   * sedeId solo lo usa Aleph, que opera sobre centros que no son suyos. Una
   * sede puede mandarlo igualmente: el backend lo ignora y se queda con la
   * sede del token.
   */
  subirFoto: async (fichero, sedeId) => {
    const formulario = new FormData();
    formulario.append('imagen', fichero);
    if (sedeId) formulario.append('sedeId', sedeId);
    const token = localStorage.getItem(CLAVE_TOKEN);
    // Sin Content-Type a propósito: lo pone el navegador con el separador
    // que genera para el formulario. Fijarlo a mano rompe la subida.
    const res = await fetch(BASE + '/api/hub/fotos', {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formulario,
    });
    const datos = await res.json().catch(() => null);
    if (!res.ok) throw new ErrorApi(datos?.error || 'No se ha podido subir la imagen.', res.status);
    return datos;
  },

  añadirFotoPorUrl: (url) => pedir('/hub/fotos', { metodo: 'POST', cuerpo: { url } }),
  quitarFoto: (id) => pedir(`/hub/fotos/${id}`, { metodo: 'DELETE' }),
  añadirServicio: (datos) => pedir('/hub/servicios', { metodo: 'POST', cuerpo: datos }),
  quitarServicio: (id) => pedir(`/hub/servicios/${id}`, { metodo: 'DELETE' }),
  añadirBono: (datos) => pedir('/hub/bonos', { metodo: 'POST', cuerpo: datos }),
  quitarBono: (id) => pedir(`/hub/bonos/${id}`, { metodo: 'DELETE' }),

  /* --- gobierno de Aleph --- */
  accesos: () => pedir('/hub/accesos'),
  crearAcceso: (datos) => pedir('/hub/accesos', { metodo: 'POST', cuerpo: datos }),
  cambiarAcceso: (id, activo) => pedir(`/hub/accesos/${id}`, { metodo: 'PATCH', cuerpo: { activo } }),

  codigos: () => pedir('/hub/codigos'),
  crearCodigo: (datos) => pedir('/hub/codigos', { metodo: 'POST', cuerpo: datos }),
  cambiarCodigo: (id, activo) => pedir(`/hub/codigos/${id}`, { metodo: 'PATCH', cuerpo: { activo } }),

  crearSede: (datos) => pedir('/hub/sedes', { metodo: 'POST', cuerpo: datos }),
  editarSede: (id, datos) => pedir(`/hub/sedes/${id}`, { metodo: 'PATCH', cuerpo: datos }),
  eliminarSede: (id) => pedir(`/hub/sedes/${id}`, { metodo: 'DELETE' }),
};
